import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Link,
  Input,
} from '@heroui/react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuthStore, validateAuthState } from '@/stores/auth.store';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TermsModal from './TermsModal';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import ForgotPasswordModal from './ForgotPasswordModal';

import canvasLogin from '@/assets/canvasLogin.webp';
import logoMacroindustrial from '@/assets/logo-macroindustrial.png';
import googleLogo from '@/assets/google.webp';
import { generateStateKey } from '@/utils/stateKeyGenerator';
import { setState } from '@/services/empresa/users.service';

interface LoginModalProps {
  enableGoogle?: boolean;
  enableMicrosoft?: boolean;
  enableFacebook?: boolean;
  onGoogleLogin?: () => void;
  onMicrosoftLogin?: () => void;
  onFacebookLogin?: () => void;
  className?: string;
  showUsername?: boolean;
  isLoggedInProp?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  hideTriggerButton?: boolean;
}


// Component that handles the login/logout button and modal
const LoginModal = ({
  enableGoogle ,
  enableMicrosoft ,
  enableFacebook ,
  onGoogleLogin,
  onMicrosoftLogin,
  onFacebookLogin,
  className = "",
  isOpen: isOpenProp,
  onClose: onCloseProp,
  hideTriggerButton = false,
}: LoginModalProps) => {
  const [checkingAuth, setCheckingAuth] = useState<boolean>(true);
  const [internalModalOpen, setInternalModalOpen] = useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState<boolean>(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState<boolean>(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const navigate = useNavigate();
  const { isLoggedIn, clearCredentials, logout } = useAuthStore();
  const { t } = useTranslation(); // Get the translation function

  // Cleanup toast when component unmounts
  useEffect(() => {
    return () => {
      setShowToast(false);
    };
  }, []);

  const isModalOpen = isOpenProp !== undefined ? isOpenProp : internalModalOpen;

  const handleCloseModal = () => {
    if (onCloseProp) {
      onCloseProp();
    } else {
      setInternalModalOpen(false);
    }
  };

  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthStatus();
    return () => {
      setCheckingAuth(false);
    };
  }, []);

  const checkAuthStatus = useCallback(() => {
    // Use the auth store to check authentication status
    const isValid = validateAuthState();
    if (!isValid) {
      clearCredentials();
    }
    setCheckingAuth(false);
  }, []);

  const handleGoogleLogin = async () => {
    if (onGoogleLogin) {
      onGoogleLogin();
    } else {
      // Default behavior: redirect to Google auth endpoint
      const stateKey = generateStateKey();
      await setState(stateKey, 'google')
      window.location.href = `${import.meta.env.VITE_API_URL}/connect/google`;
    }
  };

  const handleMicrosoftLogin = () => {
    if (onMicrosoftLogin) {
      onMicrosoftLogin();
    } else {
      // Default behavior: redirect to Microsoft auth endpoint
      window.location.href = `${import.meta.env.VITE_API_URL}/connect/microsoft`;
    }
  };

  const handleFacebookLogin = () => {
    if (onFacebookLogin) {
      onFacebookLogin();
    } else {
      // Default behavior: redirect to Facebook auth endpoint
      // You might need to implement this based on your backend setup
      window.location.href = `${import.meta.env.VITE_API_URL}/connect/facebook`;
    }
  };

  const handleLoginWithEmailPassword = async () => {
    try {
      // Import the login function from the user service
      const { loginUsuario } = await import('@/services/empresa/users.service');
      
      // Call the login function with email and password
      await loginUsuario({ email, password });
      
      // Close the modal after successful login
      handleCloseModal();
      
      // Navigate to the root path after successful login
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      setToastMessage('Error al iniciar sesión. Por favor verifica tus credenciales.');
      setToastType('error');
      setShowToast(true);
      
      // Auto-hide the toast after 5 seconds
      setTimeout(() => {
        setShowToast(false);
      }, 5000);
    }
  };

  const handleLogout = async () => {
    // Call the logout function which makes API call to Strapi and clears credentials
    await logout();
    // Close the modal after logout
    handleCloseModal();
  };

  // Check authentication status when modal is opened
  useEffect(() => {
    if (isModalOpen) {
      checkAuthStatus();
    }
  }, [isModalOpen, checkAuthStatus]);

  // Function to render the login button that appears in the navbar
  const renderNavButton = () => {
    if (checkingAuth) {
      return (
        <button className={`ml-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 ${className}`}>
          Verificando...
        </button>
      );
    }
  };

  if (checkingAuth) {
    return (
      <>
        {!hideTriggerButton && renderNavButton()}
      </>
    );
  }

  return (
    <>
      {!hideTriggerButton && renderNavButton()}
      <Modal 
          isOpen={isModalOpen} 
          onClose={handleCloseModal}
          placement="center"
          backdrop="blur"
          size="5xl"
        >
          <ModalContent className="max-w-6xl h-auto p-0 max-h-[90vh] overflow-auto">
            {/* Responsive grid: single column on mobile, two columns on desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
              {/* Left side - Image for larger screens (hidden on mobile) */}
              <div 
                className="hidden lg:flex flex-col items-center justify-center p-12 relative" 
                style={{ 
                  backgroundImage: `url(${canvasLogin})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat"
                }}
              >

                <div className="absolute inset-0 bg-white/30"></div>
                <div className="max-w-md w-full text-center relative z-10">
                  <div className="absolute top-4 left-4 z-20">
                    <img 
                      src={logoMacroindustrial} 
                      alt="Macroindustrial Logo" 
                      className="w-96 object-contain rounded-md shadow-md" 
                    />
                  </div>
                </div>
              </div>
              
              {/* Right side - Login form (visible on all screens) */}
              <div className="flex flex-col">
                <ModalHeader className="flex flex-col gap-1 p-6">
                  <div className="flex md:hidden flex-col gap-4 mb-4">
                    <p className="text-[24px] leading-[140%] max-sm:text-[20px] text-center font-bold">
                      {t('login')}
                    </p>
                    <p className="text-[12px] leading-[150%] text-heading text-center w-8/12 mx-auto max-sm:w-full">
                      {t('loginSubtitle')}
                    </p>
                  </div>
                  <div className="hidden md:flex flex-col gap-4">
                    <p className="text-[24px] leading-[140%] max-sm:text-[20px] text-center font-bold">
                      {t('login')}
                    </p>
                    <p className="text-[12px] leading-[150%] text-heading text-center w-8/12 mx-auto max-sm:w-full">
                      {t('loginSubtitle')}
                    </p>
                  </div>
                </ModalHeader>
                
                <ModalBody className=" px-6 flex-grow">
                  
                  {isLoggedIn ? (
                    // Show logout option if user is logged in
                    <div className="flex flex-col items-center py-6">
                      <p className="text-center mb-4">
                        {t('loginSuccess')}
                      </p>
                      <Button 
                        color="danger" 
                        variant="flat"
                        onPress={handleLogout}
                      >
                        {t('logout')}
                      </Button>
                    </div>
                  ) : (
                    // Show login options if user is not logged in
                    <>
                      {/* Email/password login form */}
                      <div className="flex flex-col">                        
                        <Input                          
                          label={t('email')}
                          type="email"
                          variant="bordered"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          isRequired
                        />
                        <Input
                          label={t('password')}
                          variant="bordered"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          isRequired
                          endContent={
                            <button className="focus:outline-none" type="button" onClick={togglePasswordVisibility}>
                              {isPasswordVisible ? (
                                <EyeSlashIcon className="w-5 h-5 text-gray-500" />
                              ) : (
                                <EyeIcon className="w-5 h-5 text-gray-500" />
                              )}
                            </button>
                          }
                          type={isPasswordVisible ? "text" : "password"}
                        />
                        <Button 
                          variant="solid" 
                          className="w-full"
                          onPress={handleLoginWithEmailPassword}
                          isDisabled={!email || !password}
                        >
                          {t('login')}
                        </Button>
                        
                        <div className="text-center mt-4">
                          <Link 
                            className="text-sm text-link underline cursor-pointer"
                            onPress={() => setIsForgotPasswordModalOpen(true)}
                          >
                            {t('forgotPassword')}
                          </Link>
                        </div>
                      </div>
                      
                      {/* Or separator */}
                      <div className="flex items-center my-4">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="mx-4 text-gray-500 text-sm">{t('or')}</span>
                        <div className="flex-grow border-t border-gray-300"></div>
                      </div>
                      
                      {/* Social login options */}
                      {(enableGoogle || enableMicrosoft || enableFacebook) && (
                        <div className="flex flex-col gap-2">
                          {enableGoogle && (
                            <Button 
                              variant="bordered" 
                              className="w-full"
                              startContent={
                                <img src={googleLogo} alt="Google logo" className="w-5 h-5 object-contain" />
                              }
                              onPress={handleGoogleLogin}
                            >
                              {t('google')}
                              
                            </Button>
                          )}
                          
                          {enableMicrosoft && (
                            <Button 
                              variant="bordered" 
                              className="w-full"
                              startContent={
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                  <path 
                                    fill="currentColor" 
                                    d="M11.4 24H0V12.6h11.4V24zM24 0H12.6v11.4H24V0zm-12.6 0H0v11.4h11.4V0zM24 24H12.6V12.6H24V24z"
                                  />
                                </svg>
                              }
                              onPress={handleMicrosoftLogin}
                            >
                              {t('microsoft')}
                            </Button>
                          )}
                          
                          {enableFacebook && (
                            <Button 
                              variant="bordered" 
                              className="w-full"
                              startContent={
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                  <path 
                                    fill="currentColor" 
                                    d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.593 1.323-1.325V1.325C24 .593 23.407 0 22.675 0z"
                                  />
                                </svg>
                              }
                              onPress={handleFacebookLogin}
                            >
                              {t('facebook')}
                            </Button>
                          )}
                        </div>
                      )}
                      <p className="text-[12px] leading-[150%] !text-heading !font-inter m-0 text-center mt-auto">
                        {t('loginTermsText')}{' '}
                        <Link 
                          className="text-[12px] leading-[150%] text-link underline cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            setIsTermsModalOpen(true);
                          }}
                        >
                          {t('terms')}
                        </Link>{' '}
                        {t('and')}{' '}
                        <Link 
                          className="text-[12px] leading-[150%] text-link underline cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            setIsPrivacyModalOpen(true);
                          }}
                        >
                          {t('privacyPolicy')}
                        </Link>.
                      </p>
                    </>
                  )}
                </ModalBody>
                
                <ModalFooter className="px-6 pb-6">
                </ModalFooter>
              </div>
            </div>
          </ModalContent>
          
          {/* Toast Notification - positioned inside the modal to appear above backdrop */}
          {showToast && (
            <div 
              className={`absolute top-4 right-4 z-50 p-4 rounded-md shadow-lg transition-opacity duration-300 ${
                toastType === 'error' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-green-500 text-white'
              }`}
            >
              <div className="flex items-center">
                <span>{toastMessage}</span>
                <button 
                  onClick={() => setShowToast(false)}
                  className="ml-4 text-white hover:text-gray-200 focus:outline-none"
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </Modal>
        
        {/* Terms Modal */}
        <TermsModal 
          isOpen={isTermsModalOpen} 
          onClose={() => setIsTermsModalOpen(false)} 
        />
        
        {/* Privacy Policy Modal */}
        <PrivacyPolicyModal 
          isOpen={isPrivacyModalOpen} 
          onClose={() => setIsPrivacyModalOpen(false)} 
        />
        
        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          isOpen={isForgotPasswordModalOpen}
          onClose={() => setIsForgotPasswordModalOpen(false)}
        />
    </>
  );
};

export default LoginModal;